/**
 * DelegatePortal - Self-service portal for registered delegates
 * Mobile-friendly view of QR code, breakout sessions, meals, accommodation
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import TodaysReadingWidget from '@/components/TodaysReadingWidget';
import {
  QrCode, Calendar, MapPin, Utensils, Home, Clock, Shield, User,
  CheckCircle, XCircle, ChevronRight, LogOut, Coffee, Sun, Moon
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getToken } from '@/lib/authToken';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = {
  registered: 'bg-emerald-100 text-emerald-700',
  checked_in: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  pending_payment: 'bg-amber-100 text-amber-700',
  partially_registered: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
};

const MEAL_ICONS = { breakfast: Coffee, lunch: Sun, dinner: Moon };

const DelegatePortal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initial = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    ['qr', 'sessions', 'meals', 'accommodation'].includes(initial) ? initial : 'qr'
  );

  const { data: portal, isLoading, error } = useQuery({
    queryKey: ['delegate-portal'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delegate/my-portal`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) {
        if (res.status === 401) throw new Error('Please log in to access your portal');
        throw new Error('Failed to load portal');
      }
      return res.json();
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading your portal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardContent className="p-6 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h2 className="font-semibold text-lg text-slate-900 mb-1">Access Denied</h2>
            <p className="text-sm text-slate-500 mb-4">{error.message}</p>
            <Button onClick={() => navigate('/login')} className="w-full">Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!portal) return null;

  const { delegate, event, tier, meals, breakout_sessions, accommodation } = portal;

  const tabs = [
    { id: 'qr', label: 'QR Code', icon: QrCode },
    { id: 'sessions', label: 'Sessions', icon: Calendar, count: breakout_sessions?.length },
    { id: 'meals', label: 'Meals', icon: Utensils },
    { id: 'accommodation', label: 'Room', icon: Home },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="delegate-portal">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-lg font-bold text-white" data-testid="delegate-name">{delegate.name}</h1>
              <p className="text-xs text-slate-400">{event?.name || 'Event'}</p>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="ghost" showLabel={false} />
              <Badge className={STATUS_COLORS[delegate.status] || 'bg-slate-100 text-slate-700'} data-testid="delegate-status">
                {delegate.status?.replace(/_/g, ' ')}
              </Badge>
              {delegate.checked_in && (
                <Badge className="bg-blue-500 text-white" data-testid="checked-in-badge">
                  <CheckCircle className="h-3 w-3 mr-1" /> Checked In
                </Badge>
              )}
            </div>
          </div>

          {/* Quick Info */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5 text-center">
              <p className="text-xs text-slate-400">Tier</p>
              <p className="text-sm font-semibold text-white">{tier?.name || 'General'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5 text-center">
              <p className="text-xs text-slate-400">Province</p>
              <p className="text-sm font-semibold text-white">{delegate.province || '-'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2.5 text-center">
              <p className="text-xs text-slate-400">Age Group</p>
              <p className="text-sm font-semibold text-white">{delegate.age_group || '-'}</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-white/5 rounded-xl p-1">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                  activeTab === t.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
                {t.count > 0 && <span className="bg-indigo-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 pb-8">
        <div className="max-w-lg mx-auto">

          {/* QR Code Tab */}
          {activeTab === 'qr' && (
            <div className="space-y-4" data-testid="qr-tab">
              <Card className="overflow-hidden">
                <CardContent className="p-6 flex flex-col items-center">
                  {delegate.qr_data ? (
                    <>
                      <div className="bg-white p-4 rounded-xl shadow-inner mb-3">
                        <QRCodeSVG value={delegate.qr_data} size={200} level="H" />
                      </div>
                      <p className="text-sm font-mono text-slate-500 mt-1" data-testid="badge-code">Badge: {delegate.badge_code}</p>
                      <p className="text-xs text-slate-400 mt-1">Show this QR code at check-in, meals, and security gates</p>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <QrCode className="h-16 w-16 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">QR code will be available after registration is confirmed</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Event Info */}
              {event && (
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <h3 className="font-semibold text-sm">{event.name}</h3>
                    {event.venue && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <MapPin className="h-3 w-3" /> {typeof event.venue === 'object' ? (event.venue.name || event.venue.address || 'Venue') : event.venue}
                      </div>
                    )}
                    {event.start_date && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Calendar className="h-3 w-3" /> {new Date(event.start_date).toLocaleDateString()}
                        {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString()}`}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Today's Bible reading — shared widget */}
              <TodaysReadingWidget compact />
            </div>
          )}

          {/* Breakout Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-3" data-testid="sessions-tab">
              {(!breakout_sessions || breakout_sessions.length === 0) ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No breakout sessions assigned yet</p>
                    <p className="text-xs text-slate-400 mt-1">Sessions will appear here once you're enrolled</p>
                  </CardContent>
                </Card>
              ) : (
                breakout_sessions.map((s, i) => (
                  <Card key={i} data-testid={`breakout-session-${i}`}>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm text-slate-900">{s.name}</h3>
                      <div className="mt-2 space-y-1.5">
                        {s.venue && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <MapPin className="h-3 w-3 text-indigo-400" /> {s.venue}
                          </div>
                        )}
                        {s.start_time && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Clock className="h-3 w-3 text-amber-400" /> {s.start_time}{s.end_time ? ` - ${s.end_time}` : ''}
                          </div>
                        )}
                        {s.facilitator && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <User className="h-3 w-3 text-emerald-400" /> {s.facilitator}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Meals Tab */}
          {activeTab === 'meals' && (
            <div className="space-y-3" data-testid="meals-tab">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">Meal Access</h3>
                    <Badge className={meals?.meal_access ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'} data-testid="meal-access-badge">
                      {meals?.meal_access ? 'Included' : 'Not Included'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {['breakfast', 'lunch', 'dinner'].map(meal => {
                      const MealIcon = MEAL_ICONS[meal];
                      const entitlement = typeof meals?.entitlements === 'object' ? meals.entitlements[meal] : null;
                      const isIncluded = entitlement === 'Yes' || entitlement === true || meals?.meal_access;
                      return (
                        <div key={meal} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <MealIcon className="h-4 w-4 text-amber-500" />
                            <span className="text-sm font-medium capitalize">{meal}</span>
                          </div>
                          {isIncluded ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-slate-300" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Scan History */}
              {meals?.scan_history?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2 p-4">
                    <CardTitle className="text-sm">Recent Meal Scans</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 space-y-1.5">
                    {meals.scan_history.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                        <span className="capitalize font-medium">{s.meal_type}</span>
                        <span className="text-slate-400">{s.scanned_at ? new Date(s.scanned_at).toLocaleString() : '-'}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Accommodation Tab */}
          {activeTab === 'accommodation' && (
            <div className="space-y-3" data-testid="accommodation-tab">
              {accommodation ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <Home className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg" data-testid="accommodation-building">{accommodation.building || 'Building'}</h3>
                        <p className="text-sm text-slate-500" data-testid="accommodation-room">Room {accommodation.room_number || '-'}</p>
                      </div>
                    </div>
                    {accommodation.room_type && (
                      <Badge variant="outline" className="capitalize">{accommodation.room_type}</Badge>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Home className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No accommodation assigned</p>
                    <p className="text-xs text-slate-400 mt-1">Room details will appear here once assigned</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default DelegatePortal;
